import { useEffect, useRef, useState } from 'react';
import { playHaptic } from '../haptics';
import { LOGOS, shuffle } from '../logos';
import { addBalance } from '../balance';
import { playResultSound, stopResultSounds } from '../sounds';
import { playCue } from '../cuelume';
import { GameHeader, ResultActions, ResultSticker } from '../components/GameHeader';

const COLS = 6;
const CAP = 4;
const TYPES = 4;
const MOVES_LIMIT = 40;

function columnComplete(col) {
  return col.length === CAP && col.every((tile) => tile.logoId === col[0].logoId);
}

function boardSolved(columns) {
  return columns.every((col) => col.length === 0 || col.every((tile) => tile.logoId === col[0].logoId));
}

function createColumns(uidRef) {
  for (let attempt = 0; attempt < 50; attempt += 1) {
    const companies = shuffle(LOGOS).slice(0, TYPES);
    const pool = [];
    companies.forEach((logo) => {
      for (let i = 0; i < CAP; i += 1) {
        uidRef.current += 1;
        pool.push({ uid: uidRef.current, logoId: logo.id, src: logo.src });
      }
    });
    const columns = Array.from({ length: COLS }, () => []);
    shuffle(pool).forEach((tile, index) => {
      columns[index % (COLS - 1)].push(tile);
    });
    if (!columns.some(columnComplete) && !boardSolved(columns)) return columns;
  }
  return Array.from({ length: COLS }, () => []);
}

function canPour(fromCol, toCol) {
  if (!fromCol.length || fromCol === toCol) return false;
  if (toCol.length >= CAP) return false;
  if (!toCol.length) return true;
  return toCol[toCol.length - 1].logoId === fromCol[fromCol.length - 1].logoId;
}

function SortGame({ onExit }) {
  const uidRef = useRef(0);
  const [columns, setColumns] = useState(() => createColumns(uidRef));
  const [selected, setSelected] = useState(null);
  const [movesLeft, setMovesLeft] = useState(MOVES_LIMIT);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(null);
  const resultSettled = useRef(false);

  useEffect(() => () => stopResultSounds(), []);

  useEffect(() => {
    if (!finished || resultSettled.current) return;
    resultSettled.current = true;
    if (finished === 'won') {
      playResultSound('win');
      if (score > 0) addBalance(score);
    }
    if (finished === 'lost') playResultSound('fail');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  const restart = () => {
    setColumns(createColumns(uidRef));
    setSelected(null);
    setMovesLeft(MOVES_LIMIT);
    setScore(0);
    setFinished(null);
    resultSettled.current = false;
    stopResultSounds();
    playHaptic('selection');
  };

  const handleColumn = (index) => {
    if (finished) return;
    const col = columns[index];

    if (selected === null) {
      if (!col.length) return;
      setSelected(index);
      playHaptic('light');
      playCue('tick');
      return;
    }

    if (selected === index) {
      setSelected(null);
      playCue('whisper');
      return;
    }

    const next = columns.map((item) => [...item]);
    const from = next[selected];
    const to = next[index];
    if (!canPour(from, to)) {
      setSelected(index);
      playHaptic('light');
      playCue('tick');
      return;
    }

    to.push(from.pop());
    const movesAfter = movesLeft - 1;
    const gained = columnComplete(to) ? 150 : 20;
    setColumns(next);
    setSelected(null);
    setMovesLeft(movesAfter);
    setScore((s) => s + gained);
    playHaptic(columnComplete(to) ? 'success' : 'selection');
    playCue(columnComplete(to) ? 'bloom' : 'droplet');

    if (boardSolved(next)) {
      const bonus = movesAfter * 10;
      setScore((s) => s + bonus);
      setFinished('won');
    } else if (movesAfter === 0) {
      setFinished('lost');
    }
  };

  return (
    <div className="app">
      <GameHeader title="Портфель" icon="icon-suitcase.svg" onExit={onExit} />

      <div className="score-panel">
        <div className="score-cell">
          <span className="score-value">{score}</span>
          <span className="score-caption">Очки</span>
        </div>
        <span className="score-divider" aria-hidden="true" />
        <div className="score-cell">
          <span className={`score-value${movesLeft <= 8 && !finished ? ' low' : ''}`}>{movesLeft}</span>
          <span className="score-caption">Ходы</span>
        </div>
      </div>

      <p className="game-hint">Собери одинаковые акции в одном столбике</p>

      <main className="sort-board" aria-label="Портфели">
        {columns.map((col, index) => (
          <button
            key={index}
            type="button"
            className={`sort-col${selected === index ? ' selected' : ''}${
              columnComplete(col) ? ' done' : ''
            }`}
            onClick={() => handleColumn(index)}
            aria-label={`Портфель ${index + 1}`}
          >
            <span className="sort-stack">
              {Array.from({ length: CAP }).map((_, slot) => {
                const tile = col[slot];
                return (
                  <span key={slot} className={`sort-slot${tile ? '' : ' empty'}`}>
                    {tile && <img src={tile.src} alt="" draggable="false" />}
                  </span>
                );
              })}
            </span>
          </button>
        ))}
      </main>

      {finished && (
        <div className="result" role="dialog" aria-label="Результат игры">
          <div className={`result-card${finished === 'lost' ? ' lost' : ''}`}>
            <ResultSticker won={finished === 'won'} />
            <p className="result-title">{finished === 'won' ? 'Портфель собран' : 'Ходы закончились'}</p>
            <p className="result-score">{finished === 'won' ? score : 0}</p>
            <p className="result-score-label">{finished === 'won' ? 'очков' : 'очки не начисляются'}</p>
            <ResultActions onRestart={restart} onHome={onExit} />
          </div>
        </div>
      )}
    </div>
  );
}

export default SortGame;
